/**
 * Audio Codec Utilities
 *
 * Handles audio compression and encoding for transmission.
 */

/**
 * Convert a Blob to Uint8Array
 */
export async function blobToUint8Array(blob: Blob): Promise<Uint8Array> {
  const arrayBuffer = await blob.arrayBuffer()
  return new Uint8Array(arrayBuffer)
}

/**
 * Convert Uint8Array to Blob
 */
export function uint8ArrayToBlob(
  data: Uint8Array,
  mimeType: string = 'audio/webm'
): Blob {
  // Create a copy to ensure we have a proper ArrayBuffer
  return new Blob([new Uint8Array(data)], { type: mimeType })
}

/**
 * Compress audio blob (placeholder - WebM with Opus is already compressed)
 */
export async function compressAudio(blob: Blob): Promise<Uint8Array> {
  // WebM with Opus codec is already well-compressed
  // Future: could implement additional compression if needed
  return blobToUint8Array(blob)
}

/**
 * Decompress audio data
 */
export function decompressAudio(
  data: Uint8Array,
  mimeType: string = 'audio/webm'
): Blob {
  // Direct conversion - no additional decompression needed
  return uint8ArrayToBlob(data, mimeType)
}

/**
 * Get audio duration from a blob
 */
export async function getAudioDuration(blob: Blob): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio()
    const url = URL.createObjectURL(blob)

    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(url)
      resolve(audio.duration)
    }

    audio.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load audio metadata'))
    }

    audio.src = url
  })
}

/**
 * Check if audio blob is valid
 */
export async function isValidAudio(blob: Blob): Promise<boolean> {
  try {
    const duration = await getAudioDuration(blob)
    return duration > 0 && isFinite(duration)
  } catch {
    return false
  }
}

/**
 * Trim audio to maximum duration
 * Note: This is a simple approach - for more precise trimming,
 * would need Web Audio API
 */
export async function trimAudioToMaxDuration(
  blob: Blob,
  maxSeconds: number
): Promise<Blob> {
  const duration = await getAudioDuration(blob)

  if (duration <= maxSeconds) {
    return blob
  }

  // For now, just return original - proper trimming requires Web Audio API
  console.warn(
    `[Codec] Audio exceeds max duration (${duration}s > ${maxSeconds}s)`
  )
  return blob
}

/**
 * Get approximate file size description
 */
export function getFileSizeDescription(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
